import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030';

import {
    ROLE_LIMITED_STAFF,
    ROLE_MANAGER,
    ROLE_OWNER,
    ROLE_STAFF,
} from '../../../authz/constants/roles';
import {
    DEFAULT_STAFF_MULTI_EDITOR_TAB,
    STAFF_MULTI_EDITOR_TABS,
    StaffMultiEditorTabId,
} from './staffMultiEditorTabConfig';

const STAFF_MULTI_EDITOR_TAB_IDS = new Set<StaffMultiEditorTabId>(
    STAFF_MULTI_EDITOR_TABS.map((tab) => tab.id),
);

export type StaffMultiEditorAvailabilityDeepLink = {
    action: 'set' | 'block';
    start: string;
    end: string;
};

export type StaffMultiEditorSearchParams = {
    runId: string | null;
    expectedCount: number | null;
    staffId: string | null;
    staffIds: string[] | null;
    tab: StaffMultiEditorTabId | null;
    availabilityDeepLink: StaffMultiEditorAvailabilityDeepLink | null;
    isCreateMode: boolean;
};

export const STAFF_MULTI_EDITOR_MODE_SEARCH_PARAM = 'mode';

export const STAFF_MULTI_EDITOR_CREATE_MODE = 'create';

/** Placeholder id for create-mode editor tabs before a staff record exists. */
export const STAFF_MULTI_EDITOR_CREATE_STAFF_ID = 'new-staff';

export const buildStaffMultiEditorCreateRoute = (
    pathname = '/staff-multi-editor',
): string =>
    `${pathname}?${STAFF_MULTI_EDITOR_MODE_SEARCH_PARAM}=${STAFF_MULTI_EDITOR_CREATE_MODE}`;

/** Canonical staff create entry for nav and onboarding progress links. */
export const resolveStaffCreateRoute = (
    _business: { name?: string | null } | null | undefined,
): string => buildStaffMultiEditorCreateRoute();

/** Maps legacy `/staff/create` and `/staff/:id/edit` URLs to Staff Multi Editor. */
export const buildLegacyStaffEditRedirectTarget = (
    staffId: string,
    search: string,
): string => {
    const params = new URLSearchParams(search);
    params.set('staff_id', staffId);

    if (
        (params.has('start') ||
            params.get('availabilitySet') === 'true' ||
            params.get('availabilityBlock') === 'true') &&
        !params.has('tab')
    ) {
        params.set('tab', 'hours-pay');
    }

    return `/staff-multi-editor?${params.toString()}`;
};

const parseStaffMultiEditorCreateMode = (
    params: URLSearchParams,
    runId: string | null,
): boolean => {
    if (runId) {
        return false;
    }

    return (
        params.get(STAFF_MULTI_EDITOR_MODE_SEARCH_PARAM)?.trim() ===
        STAFF_MULTI_EDITOR_CREATE_MODE
    );
};

const parseStaffMultiEditorTabParam = (
    raw: string | null,
): StaffMultiEditorTabId | null => {
    const trimmed = raw?.trim();
    if (
        !trimmed ||
        !STAFF_MULTI_EDITOR_TAB_IDS.has(trimmed as StaffMultiEditorTabId)
    ) {
        return null;
    }

    return trimmed as StaffMultiEditorTabId;
};

const parseStaffMultiEditorAvailabilityDeepLink = (
    params: URLSearchParams,
): StaffMultiEditorAvailabilityDeepLink | null => {
    const start = params.get('start')?.trim() || null;
    const end = params.get('end')?.trim() || null;
    const availabilitySet = params.get('availabilitySet') === 'true';
    const availabilityBlock = params.get('availabilityBlock') === 'true';

    if (!start || !end) {
        return null;
    }

    if (availabilitySet && !availabilityBlock) {
        return { action: 'set', start, end };
    }

    if (availabilityBlock && !availabilitySet) {
        return { action: 'block', start, end };
    }

    return null;
};

export const STAFF_MULTI_EDITOR_TRANSIENT_SEARCH_PARAM_KEYS = [
    'tab',
    'start',
    'end',
    'availabilitySet',
    'availabilityBlock',
] as const;

export const hasStaffMultiEditorTransientSearchParams = (
    search: string,
): boolean => {
    const params = new URLSearchParams(search);
    return STAFF_MULTI_EDITOR_TRANSIENT_SEARCH_PARAM_KEYS.some((key) =>
        params.has(key),
    );
};

/**
 * Remove one-time tab and availability deep-link params from the query string.
 * `mode` is stripped only on request, since switching tabs must not leave create mode.
 */
export type StripStaffMultiEditorTransientSearchParamsOptions = {
    includeCreateMode?: boolean;
    staffId?: string | null;
};

export const stripStaffMultiEditorTransientSearchParams = (
    search: string,
    {
        includeCreateMode = false,
        staffId,
    }: StripStaffMultiEditorTransientSearchParamsOptions = {},
): string => {
    const params = new URLSearchParams(search);
    for (const key of STAFF_MULTI_EDITOR_TRANSIENT_SEARCH_PARAM_KEYS) {
        params.delete(key);
    }

    if (includeCreateMode) {
        params.delete(STAFF_MULTI_EDITOR_MODE_SEARCH_PARAM);
    }

    if (staffId !== undefined) {
        if (staffId) {
            params.set('staff_id', staffId);
        } else {
            params.delete('staff_id');
        }
    }

    return params.toString();
};

export const resolveStaffMultiEditorInitialTab = (
    parsed: Pick<StaffMultiEditorSearchParams, 'tab' | 'availabilityDeepLink'>,
): StaffMultiEditorTabId => {
    if (parsed.tab) {
        return parsed.tab;
    }

    if (parsed.availabilityDeepLink) {
        return 'hours-pay';
    }

    return DEFAULT_STAFF_MULTI_EDITOR_TAB;
};

export const LIMITED_STAFF_SELF_EDIT_TAB_IDS: readonly StaffMultiEditorTabId[] =
    ['info'];

export type StaffMultiEditorSessionContext = {
    userRole: string;
    currentUserId: string | null | undefined;
    selectedStaffId: string | null;
    isCreateMode: boolean;
};

/** Limited Staff editing their own profile in the new editor (contact/basic only). */
export const isLimitedStaffSelfEditSession = ({
    userRole,
    currentUserId,
    selectedStaffId,
    isCreateMode,
}: StaffMultiEditorSessionContext): boolean =>
    userRole === ROLE_LIMITED_STAFF &&
    !isCreateMode &&
    Boolean(currentUserId) &&
    Boolean(selectedStaffId) &&
    selectedStaffId === currentUserId;

export const resolveVisibleStaffMultiEditorTabIds = (
    session: StaffMultiEditorSessionContext,
): StaffMultiEditorTabId[] => {
    if (isLimitedStaffSelfEditSession(session)) {
        return [...LIMITED_STAFF_SELF_EDIT_TAB_IDS];
    }

    return STAFF_MULTI_EDITOR_TABS.map((tab) => tab.id);
};

export const clampStaffMultiEditorTabId = (
    tabId: StaffMultiEditorTabId,
    session: StaffMultiEditorSessionContext,
): StaffMultiEditorTabId => {
    const visible = new Set(resolveVisibleStaffMultiEditorTabIds(session));
    if (visible.has(tabId)) {
        return tabId;
    }

    return DEFAULT_STAFF_MULTI_EDITOR_TAB;
};

export const resolveStaffMultiEditorInitialTabForSession = (
    parsed: Pick<StaffMultiEditorSearchParams, 'tab' | 'availabilityDeepLink'>,
    session: StaffMultiEditorSessionContext,
): StaffMultiEditorTabId =>
    clampStaffMultiEditorTabId(
        resolveStaffMultiEditorInitialTab(parsed),
        session,
    );

export const STAFF_IMPORT_RUN_RETRY_DELAYS_MS = [250, 500, 750, 1000];

/** Only apply URL availability deep links when the selected staff matches the request. */
export const resolveActiveAvailabilityDeepLink = ({
    availabilityDeepLink,
    selectedStaffId,
    requestedStaffId,
}: {
    availabilityDeepLink: StaffMultiEditorAvailabilityDeepLink | null;
    selectedStaffId: string | null;
    requestedStaffId: string | null;
}): StaffMultiEditorAvailabilityDeepLink | null => {
    if (!availabilityDeepLink || !selectedStaffId) {
        return null;
    }

    if (requestedStaffId && selectedStaffId !== requestedStaffId) {
        return null;
    }

    return availabilityDeepLink;
};

export type AvailabilityDeepLinkModalPreset = {
    startDate: string;
    fromTime: string;
    toTime: string;
};

export const buildAvailabilityDeepLinkModalPreset = ({
    start,
    end,
    timezone,
}: {
    start: string;
    end: string;
    timezone: string;
}): AvailabilityDeepLinkModalPreset => ({
    startDate: moment(start).tz(timezone).format('YYYY-MM-DD'),
    fromTime: moment(start).tz(timezone).format('HH:mm'),
    toTime: moment(end).tz(timezone).format('HH:mm'),
});

/** Calendar availability actions route to multi-editor when M5 gate and access both allow it. */
export const shouldRouteStaffAvailabilityToMultiEditor = ({
    staffMultiEditorM5Enabled,
    userRole,
    isSuperUser,
}: {
    staffMultiEditorM5Enabled: boolean;
    userRole: string;
    isSuperUser: boolean;
}): boolean =>
    staffMultiEditorM5Enabled &&
    canAccessStaffMultiEditor({
        userRole,
        isSuperUser,
        staffMultiEditorM5Enabled,
    });

/** Matches the Staff list "Multi-edit" button access rules. */
export const canAccessStaffMultiEditor = ({
    userRole,
    isSuperUser,
    staffMultiEditorM5Enabled,
}: {
    userRole: string;
    isSuperUser: boolean;
    staffMultiEditorM5Enabled: boolean;
}): boolean => {
    if (isSuperUser || userRole === ROLE_OWNER) {
        return true;
    }

    if (staffMultiEditorM5Enabled) {
        return userRole === ROLE_STAFF || userRole === ROLE_MANAGER;
    }

    return false;
};

/** Limited Staff self-service profile edit when M5 gate is on (not All Staff / other staff). */
export const canAccessStaffMultiEditorSelfEdit = ({
    userRole,
    staffMultiEditorM5Enabled,
}: {
    userRole: string;
    staffMultiEditorM5Enabled: boolean;
}): boolean =>
    staffMultiEditorM5Enabled && userRole === ROLE_LIMITED_STAFF;

export const canAccessStaffMultiEditorPage = ({
    userRole,
    isSuperUser,
    staffMultiEditorM5Enabled,
}: {
    userRole: string;
    isSuperUser: boolean;
    staffMultiEditorM5Enabled: boolean;
}): boolean =>
    canAccessStaffMultiEditor({
        userRole,
        isSuperUser,
        staffMultiEditorM5Enabled,
    }) ||
    canAccessStaffMultiEditorSelfEdit({
        userRole,
        staffMultiEditorM5Enabled,
    });

export const isLimitedStaffSelfEditOnlyAccess = ({
    userRole,
    isSuperUser,
    staffMultiEditorM5Enabled,
}: {
    userRole: string;
    isSuperUser: boolean;
    staffMultiEditorM5Enabled: boolean;
}): boolean =>
    canAccessStaffMultiEditorSelfEdit({
        userRole,
        staffMultiEditorM5Enabled,
    }) &&
    !canAccessStaffMultiEditor({
        userRole,
        isSuperUser,
        staffMultiEditorM5Enabled,
    });

const parseStaffIdsParam = (raw: string | null): string[] | null => {
    if (!raw?.trim()) {
        return null;
    }

    const staffIds = raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

    return staffIds.length > 0 ? staffIds : null;
};

export const parseStaffMultiEditorSearchParams = (
    search: string,
): StaffMultiEditorSearchParams => {
    const params = new URLSearchParams(search);
    const runId = params.get('run_id')?.trim() || null;
    const staffId = runId ? null : params.get('staff_id')?.trim() || null;
    const staffIds = runId ? null : parseStaffIdsParam(params.get('staff_ids'));
    const expectedCountRaw = params.get('expected_count');
    const tab = parseStaffMultiEditorTabParam(params.get('tab'));
    const availabilityDeepLink =
        parseStaffMultiEditorAvailabilityDeepLink(params);
    const isCreateMode = parseStaffMultiEditorCreateMode(params, runId);

    if (expectedCountRaw === null || expectedCountRaw.trim() === '') {
        return {
            runId,
            expectedCount: null,
            staffId,
            staffIds,
            tab,
            availabilityDeepLink,
            isCreateMode,
        };
    }

    const parsed = Number.parseInt(expectedCountRaw, 10);
    return {
        runId,
        expectedCount: Number.isNaN(parsed) ? null : parsed,
        staffId,
        staffIds,
        tab,
        availabilityDeepLink,
        isCreateMode,
    };
};

export const filterStaffListByIds = <T extends { id: string }>(
    staff: T[],
    staffIds: readonly string[] | null | undefined,
): T[] => {
    if (!staffIds?.length) {
        return staff;
    }

    const idSet = new Set(staffIds);
    return staff.filter((member) => idSet.has(member.id));
};

export const buildStaffMultiEditorQueryVariables = (
    business: string,
    runId: string | null,
) => ({
    business,
    search: '',
    ...(runId ? { importId: runId } : {}),
});

export const filterStaffListItems = <T extends { name: string }>(
    staff: T[],
    query: string,
): T[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return staff;
    }

    return staff.filter((member) =>
        member.name.toLowerCase().includes(normalizedQuery),
    );
};

export const shouldContinueStaffImportRunRetry = ({
    runId,
    attemptIndex,
    peopleCount,
    expectedCount,
}: {
    runId: string | null;
    attemptIndex: number;
    peopleCount: number;
    expectedCount: number | null;
}) => {
    if (!runId) {
        return false;
    }

    if (expectedCount !== null && peopleCount >= expectedCount) {
        return false;
    }

    if (expectedCount === null && peopleCount > 0) {
        return false;
    }

    return attemptIndex < STAFF_IMPORT_RUN_RETRY_DELAYS_MS.length;
};

const isStaffIdInList = (
    staffId: string | null,
    staffIds: readonly string[],
): boolean => staffId !== null && staffIds.includes(staffId);

export type ResolveStaffMultiEditorSelectionInput = {
    staffIds: readonly string[];
    /** Current staff_id from the URL — the single source of truth for selection. */
    selectedStaffId: string | null;
    runId: string | null;
    loading: boolean;
    locationSearch: string;
    appliedUrlPreselectionFor: string | null;
    isCreateMode?: boolean;
    /** Explicit selection held until the staff list catches up (post-create). */
    pinnedStaffId?: string | null;
};

export type ResolveStaffMultiEditorSelectionResult =
    | { skipUpdate: true }
    | {
          skipUpdate: false;
          selectedStaffId: string | null;
          appliedUrlPreselectionFor: string | null;
      };

/** Resolves sidebar/editor selection from URL preselection and staff list updates. */
export const resolveStaffMultiEditorSelection = ({
    staffIds,
    selectedStaffId,
    runId,
    loading,
    locationSearch,
    appliedUrlPreselectionFor,
    isCreateMode = false,
    pinnedStaffId = null,
}: ResolveStaffMultiEditorSelectionInput): ResolveStaffMultiEditorSelectionResult => {
    // Create mode never auto-selects a staff member, the same as an empty list.
    if (isCreateMode || staffIds.length === 0) {
        if (loading && staffIds.length === 0) {
            return { skipUpdate: true };
        }

        if (
            pinnedStaffId &&
            selectedStaffId === pinnedStaffId &&
            staffIds.length === 0
        ) {
            return { skipUpdate: true };
        }

        return {
            skipUpdate: false,
            selectedStaffId: null,
            appliedUrlPreselectionFor,
        };
    }

    const firstStaffId = staffIds[0];

    if (runId) {
        return {
            skipUpdate: false,
            selectedStaffId: isStaffIdInList(selectedStaffId, staffIds)
                ? selectedStaffId
                : firstStaffId,
            appliedUrlPreselectionFor,
        };
    }

    const shouldApplyUrlPreselection =
        appliedUrlPreselectionFor !== locationSearch;

    if (shouldApplyUrlPreselection && selectedStaffId) {
        return {
            skipUpdate: false,
            selectedStaffId: isStaffIdInList(selectedStaffId, staffIds)
                ? selectedStaffId
                : pinnedStaffId === selectedStaffId
                  ? selectedStaffId
                  : null,
            appliedUrlPreselectionFor: locationSearch,
        };
    }

    if (
        appliedUrlPreselectionFor !== null &&
        appliedUrlPreselectionFor !== locationSearch &&
        selectedStaffId === null &&
        !locationSearch.includes('staff_id=')
    ) {
        return {
            skipUpdate: false,
            selectedStaffId: null,
            appliedUrlPreselectionFor: locationSearch,
        };
    }

    if (
        pinnedStaffId &&
        selectedStaffId === pinnedStaffId &&
        !isStaffIdInList(pinnedStaffId, staffIds)
    ) {
        if (loading) {
            return { skipUpdate: true };
        }

        return {
            skipUpdate: false,
            selectedStaffId: pinnedStaffId,
            appliedUrlPreselectionFor,
        };
    }

    const unresolvedSelectedStaffId =
        selectedStaffId !== null && !isStaffIdInList(selectedStaffId, staffIds);

    if (
        appliedUrlPreselectionFor === locationSearch &&
        unresolvedSelectedStaffId &&
        !(pinnedStaffId && selectedStaffId === pinnedStaffId)
    ) {
        return {
            skipUpdate: false,
            selectedStaffId: null,
            appliedUrlPreselectionFor,
        };
    }

    if (
        selectedStaffId === null &&
        appliedUrlPreselectionFor === locationSearch
    ) {
        return {
            skipUpdate: false,
            selectedStaffId: null,
            appliedUrlPreselectionFor,
        };
    }

    return {
        skipUpdate: false,
        selectedStaffId: isStaffIdInList(selectedStaffId, staffIds)
            ? selectedStaffId
            : firstStaffId,
        appliedUrlPreselectionFor: shouldApplyUrlPreselection
            ? locationSearch
            : appliedUrlPreselectionFor,
    };
};

export type ConfirmLeaveCreateAction = 'reset-in-place' | 'navigate-to-create';

/** When confirming leave to Add New Staff, reset in place if already on ?mode=create. */
export const resolveConfirmLeaveCreateAction = (
    isCreateMode: boolean,
): ConfirmLeaveCreateAction =>
    isCreateMode ? 'reset-in-place' : 'navigate-to-create';

export const buildCreateFormPanelKey = (sessionKey: number): string =>
    `create-${sessionKey}`;
